import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { getCountries, getStateOptions, stateLevelLabel } from './climate.geo';
import { getSeasonOptionsForCountry, monthShort } from '../seasons/seasons.service';
import {
    SOIL_MOISTURE_UNIT,
    rainfallBandEdges,
    saveRainfallBaseline,
    saveSoilMoistureThreshold,
    validateRainfallBaseline,
    validateSoilMoistureThreshold,
} from './climate.service';

const dialogPaperSx = { sx: { borderRadius: '24px' } };

const hintSx = { fontSize: 13, color: '#625B71', mt: 1 };

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

const emptyRainfall = {
    state: '',
    seasonCode: 'KHARIF',
    baselineSeasonalRainfallMm: '',
    effectiveFrom: todayIso(),
    notes: '',
};

export function RainfallBaselineModal({ open, baseline, existing = [], onClose, onSaved }) {
    const [draft, setDraft] = useState(emptyRainfall);
    const [issues, setIssues] = useState({});
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const [countries, setCountries] = useState([]);
    const [countryId, setCountryId] = useState('');
    const [states, setStates] = useState([]);
    const [loadingGeo, setLoadingGeo] = useState(false);
    const [geoNote, setGeoNote] = useState('');
    const [seasonOptions, setSeasonOptions] = useState([]);
    const [seasonsConfigured, setSeasonsConfigured] = useState(false);
    // Bumped by the retry link so a transient geo failure can be re-attempted without reopening.
    const [geoAttempt, setGeoAttempt] = useState(0);
    // Falls back to a text field when geo has nothing to offer, so a missing or unreachable
    // country hierarchy cannot make this screen a dead end.
    const [manualState, setManualState] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDraft(baseline ? { ...baseline } : emptyRainfall);
        setIssues({});
        setError('');
        setSaving(false);
        setManualState(false);
    }, [open, baseline]);

    useEffect(() => {
        if (!open) return;
        let active = true;

        (async () => {
            setLoadingGeo(true);
            const { countries: list, failed } = await getCountries();
            if (!active) return;

            setCountries(list);
            // Always land on a country when there is one. Leaving this empty for a multi-country
            // install is what left the dropdown disabled on open with nothing explaining why.
            setCountryId((current) => {
                if (baseline?.countryId && list.some((c) => c.id === baseline.countryId)) {
                    return baseline.countryId;
                }
                return list.some((c) => c.id === current) ? current : list[0]?.id || '';
            });

            if (failed) {
                setGeoNote('Could not load the country list.');
                setManualState(true);
            } else if (list.length === 0) {
                setGeoNote('No active countries are configured.');
                setManualState(true);
            }
            setLoadingGeo(false);
        })();

        return () => {
            active = false;
        };
    }, [open, baseline, geoAttempt]);

    useEffect(() => {
        if (!open || !countryId) {
            setStates([]);
            return undefined;
        }
        let active = true;

        (async () => {
            setLoadingGeo(true);
            const { options, failed } = await getStateOptions(countryId);
            if (!active) return;

            setStates(options);
            setLoadingGeo(false);

            // An empty dropdown needs a reason next to it, and the text field back, otherwise the
            // operator is stuck on a disabled control with nothing to act on.
            if (failed) {
                setGeoNote('Could not load the geography for this country.');
                setManualState(true);
            } else if (options.length === 0) {
                setGeoNote('This country has no top-level locations yet — import or add its geography first.');
                setManualState(true);
            } else {
                setGeoNote('');
            }
        })();

        return () => {
            active = false;
        };
    }, [open, countryId, geoAttempt]);

    useEffect(() => {
        if (!open) return undefined;
        let active = true;

        (async () => {
            const { seasons, configured } = await getSeasonOptionsForCountry(countryId);
            if (!active) return;
            setSeasonOptions(seasons);
            setSeasonsConfigured(configured);

            // Keep the draft on a season this country actually has, otherwise a baseline would be
            // saved against a season code the calendar no longer contains.
            setDraft((prev) => (seasons.some((season) => season.code === prev.seasonCode)
                ? prev
                : { ...prev, seasonCode: seasons[0]?.code || '' }));
        })();

        return () => {
            active = false;
        };
    }, [open, countryId]);

    const selectedCountry = useMemo(
        () => countries.find((country) => country.id === countryId) || null,
        [countries, countryId]
    );
    const stateLabel = stateLevelLabel(selectedCountry);

    // An existing baseline whose state is no longer in the list (renamed, or entered before this
    // dropdown existed) must stay editable and keep showing its value.
    const knownState = states.some((option) => option.name === draft.state);
    const useTextField =
        manualState
        || !countryId
        || (Boolean(draft.state) && states.length > 0 && !knownState);

    const set = (field) => (event) => {
        setDraft((prev) => ({ ...prev, [field]: event.target.value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = async () => {
        const found = validateRainfallBaseline(draft, existing);
        setIssues(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const result = await saveRainfallBaseline(draft);
        setSaving(false);

        if (!result.success) {
            setError(result.error.message);
            return;
        }
        onSaved(result.data);
    };

    const edges = rainfallBandEdges(draft.baselineSeasonalRainfallMm);
    const showPreview = Number(draft.baselineSeasonalRainfallMm) > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={dialogPaperSx}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {baseline ? 'Edit rainfall baseline' : 'Add rainfall baseline'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#625B71', fontSize: 15, mb: 3 }}>
                    The long-term mean seasonal rainfall that C1_RAINFALL_DEFICIT compares each farmer's
                    observed NASA POWER rainfall against.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert> : null}

                <Box sx={{ mb: 2.5 }}>
                    {countries.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <FieldLabel label="Country" />
                            <TextField
                                select
                                fullWidth
                                value={countryId}
                                onChange={(event) => {
                                    setCountryId(event.target.value);
                                    setDraft((prev) => ({ ...prev, state: '', stateId: null, countryId: event.target.value }));
                                    setManualState(false);
                                    setGeoNote('');
                                }}
                                sx={formFieldSx}
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country.id} value={country.id}>
                                        {country.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}

                    <FieldLabel label={stateLabel} required />
                    {useTextField ? (
                        <TextField
                            fullWidth
                            value={draft.state}
                            onChange={(event) => {
                                // Typed by hand, so there is no administrative location to point at.
                                setDraft((prev) => ({
                                    ...prev,
                                    state: event.target.value,
                                    stateId: null,
                                    countryId: countryId || null,
                                }));
                                setIssues((prev) => ({ ...prev, state: undefined }));
                            }}
                            placeholder="Odisha"
                            error={Boolean(issues.state)}
                            helperText={issues.state}
                            sx={formFieldSx}
                        />
                    ) : (
                        <TextField
                            select
                            fullWidth
                            value={knownState ? draft.state : ''}
                            onChange={(event) => {
                                const picked = states.find((option) => option.name === event.target.value);
                                setDraft((prev) => ({
                                    ...prev,
                                    state: event.target.value,
                                    stateId: picked?.id || null,
                                    countryId,
                                }));
                                setIssues((prev) => ({ ...prev, state: undefined }));
                            }}
                            disabled={loadingGeo || !countryId || states.length === 0}
                            error={Boolean(issues.state)}
                            helperText={issues.state}
                            SelectProps={{ displayEmpty: true }}
                            sx={formFieldSx}
                        >
                            <MenuItem value="" disabled>
                                {loadingGeo ? 'Loading…' : `Select a ${stateLabel.toLowerCase()}`}
                            </MenuItem>
                            {states.map((option) => (
                                <MenuItem key={option.id} value={option.name}>
                                    {option.name}
                                    {option.active ? '' : ' (inactive)'}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <Typography sx={hintSx}>
                        {loadingGeo ? (
                            <>
                                <CircularProgress size={12} sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Loading {stateLabel.toLowerCase()}s from the country geography…
                            </>
                        ) : (
                            <>
                                {geoNote ? (
                                    <Box component="span" sx={{ color: '#8A5A00' }}>{geoNote} </Box>
                                ) : null}
                                Must match the farmer's location exactly — the engine looks this up by name, and a
                                mismatch silently skips the signal rather than erroring.
                                {/* Always offered: this link is the way out of an empty or failed list, so gating
                                    it on the list having loaded is what made the empty dropdown a dead end. */}
                                {' '}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        if (useTextField && states.length === 0) {
                                            // Nothing to switch to yet — re-run the lookup instead of
                                            // flipping to an empty dropdown.
                                            setManualState(false);
                                            setGeoNote('');
                                            setGeoAttempt((n) => n + 1);
                                            return;
                                        }
                                        setManualState(!useTextField);
                                        setDraft((prev) => ({ ...prev, state: '', stateId: null }));
                                    }}
                                    sx={{ color: '#294190', fontSize: 13, verticalAlign: 'baseline' }}
                                >
                                    {useTextField
                                        ? (states.length > 0 ? 'Choose from the list' : `Retry loading ${stateLabel.toLowerCase()}s`)
                                        : 'Enter a name manually'}
                                </Link>
                            </>
                        )}
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <FieldLabel label="Season" required />
                        <TextField
                            select
                            fullWidth
                            value={draft.seasonCode}
                            onChange={set('seasonCode')}
                            error={Boolean(issues.seasonCode)}
                            helperText={issues.seasonCode}
                            sx={formFieldSx}
                        >
                            {seasonOptions.map((season) => (
                                <MenuItem key={season.code} value={season.code}>
                                    {season.label} ({monthShort(season.startMonth)}–{monthShort(season.endMonth)})
                                </MenuItem>
                            ))}
                        </TextField>
                        {!seasonsConfigured && (
                            <Typography sx={hintSx}>
                                Using the engine's built-in seasons. Set this country's own calendar under
                                Configuration → Seasons.
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <FieldLabel label="Baseline rainfall (mm)" required />
                        <TextField
                            fullWidth
                            type="number"
                            value={draft.baselineSeasonalRainfallMm}
                            onChange={set('baselineSeasonalRainfallMm')}
                            inputProps={{ min: 0, step: 10 }}
                            placeholder="1150"
                            error={Boolean(issues.baselineSeasonalRainfallMm)}
                            helperText={issues.baselineSeasonalRainfallMm}
                            sx={formFieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Effective from" required />
                    <TextField
                        fullWidth
                        type="date"
                        value={draft.effectiveFrom}
                        onChange={set('effectiveFrom')}
                        error={Boolean(issues.effectiveFrom)}
                        helperText={issues.effectiveFrom}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>
                        The engine picks the most recent baseline dated on or before the assessment, so a revision
                        is a new row rather than an edit to this one.
                    </Typography>
                </Box>

                {showPreview && (
                    <Box sx={{ backgroundColor: '#F8FAFF', border: '1px solid #DFE5F6', borderRadius: '12px', p: 2, mb: 2.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#294190', mb: 0.5 }}>
                            What this baseline means
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: '#5B6472' }}>
                            A season below <strong>{Math.round(edges.mild)} mm</strong> scores risk 3, below{' '}
                            <strong>{Math.round(edges.marked)} mm</strong> scores 6, and below that scores 9.
                        </Typography>
                    </Box>
                )}

                <Box>
                    <FieldLabel label="Notes" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={draft.notes}
                        onChange={set('notes')}
                        placeholder="Source of this figure and who signed it off"
                        sx={formFieldSx}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Saving…' : 'Save baseline'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const emptyThreshold = { cropName: '', requiredThreshold: '', notes: '' };

export function SoilMoistureThresholdModal({ open, threshold, existing = [], onClose, onSaved }) {
    const [draft, setDraft] = useState(emptyThreshold);
    const [issues, setIssues] = useState({});
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDraft(threshold ? { ...threshold } : emptyThreshold);
        setIssues({});
        setError('');
        setSaving(false);
    }, [open, threshold]);

    const set = (field) => (event) => {
        setDraft((prev) => ({ ...prev, [field]: event.target.value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = async () => {
        const found = validateSoilMoistureThreshold(draft, existing);
        setIssues(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const result = await saveSoilMoistureThreshold(draft);
        setSaving(false);

        if (!result.success) {
            setError(result.error.message);
            return;
        }
        onSaved(result.data);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={dialogPaperSx}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {threshold?.id ? 'Edit soil moisture threshold' : 'Add soil moisture threshold'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#625B71', fontSize: 15, mb: 3 }}>
                    The root-zone wetness a crop needs for irrigation to count as adequate.
                    F5_SOIL_MOISTURE_ADEQUACY compares the farmer's trailing 30-day average against it.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert> : null}

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Crop" required />
                    <TextField
                        fullWidth
                        value={draft.cropName}
                        onChange={set('cropName')}
                        placeholder="Wheat"
                        error={Boolean(issues.cropName)}
                        helperText={issues.cropName}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>
                        Matched to the crop record by name when no crop id is linked, so it should match the name
                        used in Crops.
                    </Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label={`Required threshold (${SOIL_MOISTURE_UNIT})`} required />
                    <TextField
                        fullWidth
                        type="number"
                        value={draft.requiredThreshold}
                        onChange={set('requiredThreshold')}
                        inputProps={{ min: 0, max: 1, step: 0.05 }}
                        placeholder="0.60"
                        error={Boolean(issues.requiredThreshold)}
                        helperText={issues.requiredThreshold}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>
                        NASA POWER GWETROOT is a fraction of saturation between 0 and 1 — enter 0.60, not 60.
                    </Typography>
                </Box>

                <Box>
                    <FieldLabel label="Notes" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={draft.notes}
                        onChange={set('notes')}
                        placeholder="Source of this figure and who signed it off"
                        sx={formFieldSx}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Saving…' : 'Save threshold'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
